import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ecommerceServer } from '../../server';
import { Link } from 'react-router-dom';
import {
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  StarIcon,
} from '@heroicons/react/24/outline';


const ShopBySellerProds = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [sortMode, setSortMode] = useState('relevant');

  useEffect(() => {
    const fetchSellersWithProducts = async () => {
      try {
        const [allSellersResponse, sellersWithProductsResponse] = await Promise.allSettled([
          axios.get(`${ecommerceServer}/shop/all-sellers`, { withCredentials: true }),
          axios.get(`${ecommerceServer}/shop/sellers-with-products`, { withCredentials: true }),
        ]);

        const allSellers = allSellersResponse.status === 'fulfilled'
          ? allSellersResponse.value.data?.sellers || []
          : [];
        const sellersWithProducts = sellersWithProductsResponse.status === 'fulfilled'
          ? sellersWithProductsResponse.value.data?.sellers || []
          : [];

        if (allSellersResponse.status === 'rejected' && sellersWithProductsResponse.status === 'rejected') {
          throw allSellersResponse.reason || sellersWithProductsResponse.reason;
        }

        const sellersById = new Map();

        [...allSellers, ...sellersWithProducts].forEach((seller) => {
          if (!seller?._id) {
            return;
          }

          sellersById.set(seller._id, {
            ...sellersById.get(seller._id),
            ...seller,
            products: seller.products || sellersById.get(seller._id)?.products || [],
          });
        });

        setSellers([...sellersById.values()]);
      } catch (error) {
        console.error('Error fetching sellers with products:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchSellersWithProducts();
  }, []);

  const filteredSellers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

    return sellers
      .filter((seller) => {
        const productCount = seller.products?.length || seller.totalProducts || 0;
        const createdTime = seller.createdAt ? new Date(seller.createdAt).getTime() : 0;
        const matchesSearch = !normalizedSearch || [
          seller.name,
          seller.handle,
          seller.address,
          seller.description,
        ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));

        if (!matchesSearch) {
          return false;
        }

        if (filterMode === 'with-products') {
          return productCount > 0;
        }

        if (filterMode === 'new') {
          return createdTime >= thirtyDaysAgo;
        }

        return true;
      })
      .sort((sellerA, sellerB) => {
        const productCountA = sellerA.products?.length || sellerA.totalProducts || 0;
        const productCountB = sellerB.products?.length || sellerB.totalProducts || 0;
        const createdA = sellerA.createdAt ? new Date(sellerA.createdAt).getTime() : 0;
        const createdB = sellerB.createdAt ? new Date(sellerB.createdAt).getTime() : 0;

        if (sortMode === 'newest') {
          return createdB - createdA;
        }

        if (sortMode === 'products') {
          return productCountB - productCountA;
        }

        if (sortMode === 'name') {
          return String(sellerA.name || '').localeCompare(String(sellerB.name || ''));
        }

        return (productCountB - productCountA) || (createdB - createdA);
      });
  }, [filterMode, searchTerm, sellers, sortMode]);

  if (loading) {
    return <div className="shop-prods-loading">Loading...</div>;
  }

  if (error) {
    return <div className="shop-prods-error">Failed to load shops. Please try again later.</div>;
  }

  return (
    <div className="shop-prods-directory-shell">
      <section className="shop-prods-search-panel" aria-label="Shop search">
        <div className="shop-prods-search-titleblock">
          <p>Shops</p>
          <h1>Find a Shop</h1>
          <span>{filteredSellers.length} shop{filteredSellers.length === 1 ? '' : 's'} available</span>
        </div>

        <div className="shop-prods-search-tools">
          <label className="shop-prods-search-input-shell">
            <MagnifyingGlassIcon aria-hidden="true" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by shop, handle, location, or details"
            />
          </label>

          <div className="shop-prods-filter-tools" aria-label="Shop filters">
            <span><AdjustmentsHorizontalIcon aria-hidden="true" /> Filters</span>
            {[
              ['all', 'All'],
              ['with-products', 'With Products'],
              ['new', 'New Shops'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={filterMode === value ? 'active' : ''}
                onClick={() => setFilterMode(value)}
              >
                {label}
              </button>
            ))}
          </div>

          <label className="shop-prods-sort-shell">
            <span>Sort</span>
            <select value={sortMode} onChange={(event) => setSortMode(event.target.value)}>
              <option value="relevant">Relevant</option>
              <option value="newest">Newest</option>
              <option value="products">Most Products</option>
              <option value="name">Name</option>
            </select>
          </label>
        </div>
      </section>

      <div className="shop-prods-compact-grid">
        {filteredSellers.map((seller) => {
          const productCount = seller.products?.length || seller.totalProducts || 0;
          const joinedDate = seller.createdAt
            ? new Date(seller.createdAt).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
            : '';

          return (
            <Link to={seller.handle ? `/shop/${seller.handle}` : '#'} key={seller._id} className="shop-prods-compact-card">
              <img src={seller.banner?.url || '/placeholder-banner.jpg'} alt="" className="shop-prods-compact-banner" />
              <div className="shop-prods-compact-body">
                <img src={seller.avatar?.url || '/placeholder-avatar.jpg'} alt="" className="shop-prods-compact-avatar" />
                <div className="shop-prods-compact-copy">
                  <h3>{seller.name || 'Unnamed Shop'}</h3>
                  <p>{seller.handle ? `@${seller.handle}` : 'No handle listed'}</p>
                </div>
              </div>
              <div className="shop-prods-compact-meta">
                <span>{productCount} product{productCount === 1 ? '' : 's'}</span>
                {seller.address && <span><MapPinIcon aria-hidden="true" /> {seller.address}</span>}
                <span><StarIcon aria-hidden="true" /> {seller.rating || 'New'}</span>
                {joinedDate && <span>{joinedDate}</span>}
              </div>
            </Link>
          );
        })}
      </div>

      {filteredSellers.length === 0 && (
        <div className="shop-prods-empty-state">
          <h3>No shops found</h3>
          <p>Try a broader search or clear the active filters.</p>
        </div>
      )}
    </div>
  );
};

export default ShopBySellerProds;



